<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import AdvisingSessions from "@/components/MyStudentDetail/AdvisingSession/index.vue";
import BreadCrumb from "@/components/shared/BreadCrumb.vue";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import {
  hasAiAdvisingChatbotEnabled,
  hasMyStudentsToDosEnabled,
  hasTodoViewSubmissionPermission,
} from "@/composables/authorization.js";
import { useApiEmployeePermissions } from "@/composables/useApiEmployeePermissions";
import { ACADEMIC_YEARS } from "@/constants.js";
import { useGetTodosSubmissions } from "@/providers/todosSubmissions/useGetTodosSubmissions";
import cipCodesService from "@/services/cipCodes";
import countriesService from "@/services/countries";
import housingService from "@/services/housings";
import languagesService from "@/services/languages";
import learnerTypesServices from "@/services/learnerTypes";
import ordersService from "@/services/orders";
import profileService from "@/services/profile";
import userService from "@/services/user";
import { formatDateUtc } from "@/util/date";
import ActionButton from "./ActionButton.vue";
import DetailsSection from "./DetailsSection.vue";
import ErrorMessage from "./ErrorMessage.vue";
import MyStudents403Forbidden from "./MyStudents403Forbidden.vue";
import ProgramSessionCollapsiblePanel from "./ProgramSessionCollapsiblePanel.vue";
import RecentActivity from "./RecentActivity.vue";
import StudentDetailsSidebar from "./StudentDetailsSidebar.vue";
import StudentProfileHeader from "./StudentProfileHeader.vue";

const { hasApiEmployeeStudentViewPermission } = useApiEmployeePermissions();
const store = useStore();
const router = useRouter();
const route = useRoute();
const oktaId = computed(() => route?.params?.oktaId);

const error403Forbidden = ref(false);
const studentData = ref(null);
const experiences = ref([]);
const orderIds = ref([]);
const profileOrientations = ref([]);
const homeInstitutionName = ref(null);
const majorName = ref(null);
const minorName = ref(null);
const languages = ref([]);
const countries = ref([]);
const academicLevel = ref(null);
const error = ref(null);
const learnerType = ref("");
const dataLoaded = ref(false);

const todosSectionEnabled = computed(() => hasMyStudentsToDosEnabled.value);
const canViewSubmissions = computed(() => hasTodoViewSubmissionPermission.value);

const { isLoading: todosSubmissionsLoading, data: todosSubmissionsData } = useGetTodosSubmissions(oktaId, {
  enabled: () => todosSectionEnabled.value,
});

const todosSubmissions = computed(() => todosSubmissionsData.value ?? []);

const profileHeaderData = computed(() => ({
  name:
    studentData.value?.legal_first_name && studentData.value?.last_name
      ? `${studentData.value.legal_first_name} ${studentData.value.last_name}`
      : null,
  pronouns: studentData.value?.pronouns,
  email: studentData.value?.email,
  phone: studentData.value?.phone_number,
  parentGuardian: formattedParentInfo.value,
  preferredName: studentData.value?.preferred_first_name,
  birthday: formattedBirthday.value,
}));

const profileHeaderFields = [
  { label: "Pronouns", key: "pronouns" },
  { label: "Email", key: "email" },
  { label: "Phone", key: "phone" },
  { label: "Parent/Legal Guardian", key: "parentGuardian" },
  { label: "Preferred Name", key: "preferredName" },
  { label: "Birthday", key: "birthday", formatType: "date" },
];

const profileSidebarData = computed(() => ({
  ...mapField("Home University", homeInstitutionName.value),
  ...mapField("Learner Type", learnerType.value),
  ...mapField("GPA", studentData.value?.cumulative_gpa),
  ...mapField("Major", majorName.value),
  ...mapField("Minors", minorName.value),
  ...mapField("Language", languages.value.join(", ")),
  ...mapField("Citizenship", formattedCountries.value),
  ...mapField("Year in School at Application", academicLevel.value),
  ...mapField("Expected Graduation Date", studentData.value?.expected_graduation_date),
  ...mapField("Gender", studentData.value?.gender_identity),
  ...mapField("Current Address", formatAddress("current")),
  ...mapField("Permanent Address", formatAddress("permanent")),
  ...mapField("EULA Acceptance", eulaAcceptanceText.value),
  ...mapField("Participant ID", studentData.value?.okta_id),
}));

const filteredSidebarData = computed(() =>
  Object.keys(profileSidebarData.value).reduce((acc, key) => {
    if (profileSidebarData.value[key]) {
      acc[key] = profileSidebarData.value[key];
    }
    return acc;
  }, {}),
);

const activityData = computed(() => ({
  accountCreatedDate: formattedAccountCreatedDate.value,
}));

const formattedCountries = computed(() => countries.value.join(", "));
const eulaAcceptanceText = computed(() =>
  studentData.value?.terms_and_eula_acceptance === true ? "Accepted" : "Not Accepted",
);

const formattedParentInfo = computed(() => {
  const parents = studentData.value?.parents || [];
  return parents.length ? parents.map((parent) => `${parent.name} (${parent.email})`).join(", ") : "";
});

const formattedBirthday = computed(() => {
  const birthday = studentData.value?.birthdate;
  return birthday ? formatDateUtc(birthday) : null;
});

const orientations = computed(() => formatProfileOrientations(profileOrientations.value));

const fetchStudentProfile = async () => {
  try {
    studentData.value = await profileService.getMyStudentsProfile(oktaId.value);
  } catch (err) {
    if (err?.response?.status === 403) {
      error403Forbidden.value = true;
    }
    error.value = {
      message: "Failed to load student profile",
      details: err.response?.data || err.message || err,
    };
    throw err;
  }
};

const fetchExperiences = async (id) => {
  try {
    const response = await ordersService.getMyStudentsOrders(id);
    experiences.value = response.map((item) => ({
      status: item.order.status,
      program: item.order.session_name,
      programId: item.order.session_id,
      applicationId: item.order.application_id,
      term: item.order.session_id,
      programUrl:
        item.order.program_page_id && item.order.session_id
          ? `/program-page/${item.order.program_page_id}?session-id=${item.order.session_id}`
          : null,
      orderTotal: item.order.student_total_price_in_cents / 100,
      lastUpdated: formatDateUtc(item.order.updated_at),
      createdDate: formatDateUtc(item.order.created_at),
      orderId: item.order.id,
      order: {
        id: item?.order?.id,
        homeInstitutionId: item?.order?.home_institution_id,
        status: item?.order?.status,
        events: item?.product_details?.event_occurrences ?? [],
        basePrice: item?.order?.base_price ?? {},
      },
      displayStartingPrice: item?.order?.program_page?.display_starting_price,
    }));
    orderIds.value = response.map((item) => item.order.id);
  } catch (error) {
    console.error("Error fetching experiences:", error);
    experiences.value = [];
  }
};

const fetchProfileOrientations = async (participantId, orderIds) => {
  try {
    const response = await profileService.listMyStudentsOrientations(participantId, orderIds);
    profileOrientations.value = response.data.data || [];
  } catch (error) {
    console.error("Error fetching profile orientations:", error);
  }
};

const fetchLearnerType = async () => {
  if (!studentData.value.learner_type_id) return;
  try {
    const response = await learnerTypesServices.list();
    const matchedLearnerType = response.items.find((type) => type.id === studentData.value.learner_type_id);
    learnerType.value = matchedLearnerType ? matchedLearnerType.value : "Unknown";
  } catch (error) {
    console.error("Error fetching learner type:", error);
  }
};

const fetchInstitutionName = async (collegeId) => {
  if (!collegeId) return;
  try {
    const [response] = await housingService.getHomeInstitutionsById([collegeId]);
    homeInstitutionName.value = response?.data?.data?.items?.name || null;
  } catch {
    homeInstitutionName.value = null;
  }
};

const fetchAcademicDetails = async () => {
  const cipCodes = [
    { code: studentData.value.majors?.[0]?.cip_code, type: "major" },
    { code: studentData.value.minors?.[0]?.cip_code, type: "minor" },
  ].filter((item) => item.code);

  const fetchTasks = [];
  cipCodes.forEach(({ code, type }) => {
    fetchTasks.push(fetchCipCodeDetails(code, type));
  });

  await Promise.allSettled(fetchTasks);
};

const fetchCipCodeDetails = async (cipCode, type) => {
  if (!cipCode) return;
  const response = await cipCodesService.getOne(cipCode);
  if (type === "major") majorName.value = response?.cip_title || null;
  if (type === "minor") minorName.value = response?.cip_title || null;
};

const fetchLanguages = async () => {
  const studentLanguages = studentData.value.languages?.map((lang) => lang.language_id) || [];
  if (!studentLanguages.length) return;

  let page = 1;
  const uniqueLanguages = [];
  let hasMoreLanguages = true;

  while (hasMoreLanguages) {
    const newLanguages = await languagesService.list(18, page);
    if (!newLanguages.length) {
      hasMoreLanguages = false;
      break;
    }

    const matchedLanguages = newLanguages.filter((lang) => studentLanguages.includes(lang.id));

    matchedLanguages.forEach((lang) => {
      if (!uniqueLanguages.includes(lang.value)) {
        uniqueLanguages.push(lang.value);
      }
    });

    if (uniqueLanguages.length >= studentLanguages.length) {
      hasMoreLanguages = false;
      break;
    }

    page++;
  }

  languages.value = uniqueLanguages;
};

const fetchCountries = async () => {
  const countryIds = [
    studentData.value.primary_citizenship_country_id,
    studentData.value.dual_citizenship_country_first_id,
    studentData.value.dual_citizenship_country_second_id,
  ].filter(Boolean);

  try {
    const responses = await Promise.allSettled(countryIds.map((id) => countriesService.getCountry(id)));

    countries.value = responses
      .filter((res) => res.status === "fulfilled")
      .map((res) => res.value?.data?.data?.name || "")
      .filter(Boolean);
  } catch {
    countries.value = [];
  }
};

const fetchStudentData = async () => {
  try {
    await fetchStudentProfile();

    if (!error.value) {
      mapAcademicLevel();

      await Promise.allSettled([
        fetchExperiences(oktaId.value).then(() => {
          fetchProfileOrientations(oktaId.value, orderIds.value);
        }),
        fetchLearnerType(),
        fetchAcademicDetails(),
        fetchLanguages(),
        fetchCountries(),
        fetchInstitutionName(studentData.value.colleges?.[0]?.college_id),
      ]);

      dataLoaded.value = true;
    }
  } catch (err) {
    console.error("Error fetching student data:", err);
  }
};

const mapField = (label, value) => value && { [label]: value };

const formatProfileOrientations = (orientations) => {
  return orientations.flatMap((orientationItem) => {
    return orientationItem.orientation.modules.map((module) => {
      const matchedProfileModule = orientationItem.profile_orientation.profile_modules.find(
        (profileModule) => profileModule.module_id === module.module.id,
      );

      const moduleStatus = matchedProfileModule ? capitalizeFirstLetter(matchedProfileModule.current_status) : "N/A";

      return {
        orientationName: orientationItem.orientation.name,
        moduleName: module.module.name,
        moduleStatus: moduleStatus,
        orderId: orientationItem.profile_orientation.order_id,
      };
    });
  });
};

const formattedAccountCreatedDate = computed(() => {
  const date = studentData.value?.created_at;
  return date ? formatDateUtc(date, "MMM dd, yyyy, h:mm a") : null;
});

const formatAddress = (type) => {
  const address = [
    studentData.value[`${type}_address_line_1`],
    studentData.value[`${type}_address_city`],
    studentData.value[`${type}_address_state`],
    studentData.value[`${type}_address_postal_code`],
  ]
    .filter(Boolean)
    .join(", ");
  return address || "";
};

const mapAcademicLevel = () => {
  const studentAcademicLevel = studentData.value?.year_in_school_at_time_of_api_program;
  const matchedLevel = ACADEMIC_YEARS.find((level) => [level.value, level.oldValue].includes(studentAcademicLevel));
  academicLevel.value = matchedLevel ? matchedLevel.label : null;
};

const capitalizeFirstLetter = (string) => string.charAt(0).toUpperCase() + string.slice(1);

const impersonateStudent = async () => {
  try {
    const response = await userService.impersonate(studentData.value.email);
    const { currentUser, token, studentFormioResource } = response;
    localStorage.setItem("formioUser", JSON.stringify(studentFormioResource));
    localStorage.setItem("formioToken", token);
    sessionStorage.setItem("impersonationModeReturnUrl", route.fullPath);
    store.commit("setFormioUser");
    store.commit("setCurrentUser", currentUser);
    router.push({ path: "/applications" });
  } catch (error) {
    console.error("Error impersonating student:", error);
  }
};

const fieldsToCheck = {
  studentData,
  experiences,
  orientations: profileOrientations,
  formattedAccountCreatedDate,
  homeInstitutionName,
  majorName,
  minorName,
  languages,
  countries,
  academicLevel,
  learnerType,
};

const loadStudentData = async () => {
  const cachedData = store.getters["myStudentDetail/getStudentById"](oktaId.value);
  let hasChanges = false;

  if (cachedData) {
    Object.entries(fieldsToCheck).forEach(([key, fieldRef]) => {
      fieldRef.value = cachedData[key];
    });
  }

  await fetchStudentData();

  if (cachedData) {
    Object.entries(fieldsToCheck).forEach(([key, fieldRef]) => {
      if (JSON.stringify(fieldRef.value) !== JSON.stringify(cachedData[key])) {
        console.log(`Field updated: ${key}`, {
          oldValue: cachedData[key],
          newValue: fieldRef.value,
        });
        hasChanges = true;
      }
    });

    if (hasChanges) {
      store.dispatch("myStudentDetail/updateStudent", {
        oktaId: oktaId.value,
        ...Object.fromEntries(Object.entries(fieldsToCheck).map(([key, ref]) => [key, ref.value])),
      });
    }
  } else {
    store.dispatch("myStudentDetail/addStudent", {
      oktaId: oktaId.value,
      ...Object.fromEntries(Object.entries(fieldsToCheck).map(([key, ref]) => [key, ref.value])),
    });
  }
};

onMounted(async () => {
  if (!oktaId.value) {
    router.push({ name: "landing-page" });
    return;
  }

  await loadStudentData();
});
</script>
<template>
  <div class="p-5 bg-gray-50" data-testid="student-detail">
    <MyStudents403Forbidden v-if="error403Forbidden" />
    <div v-else-if="!error" class="flex flex-col md:flex-row">
      <div class="w-full p-4 space-y-6" data-testid="student-detail-main-content">
        <BreadCrumb custom-spacer experience-page data-testid="breadcrumb-navigation">
          /
        </BreadCrumb>
        <div class="font-montserrat text-university-primary flex">
          <router-link
            class="text-opacity-70 text-sm"
            :to="{
              name: 'student-management',
              query: route.query,
              params: { page: 'listing' },
            }"
            >My Students</router-link
          >
          <ArrowRight custom-class="w-4 mr-1 ml-1" />
          <router-link
            class="font-medium text-sm"
            :to="{
              name: 'student-management',
              query: route.query,
              params: { page: 'student-detail', oktaId: oktaId },
            }"
            >Student Details</router-link
          >
        </div>
        <SkeletonLoader
          v-if="!Object.values(profileHeaderData).some((value) => value)"
          height="h-28"
          width="w-full"
          data-testid="student-detail-profile-header-loader"
        />
        <StudentProfileHeader
          v-else
          :data="profileHeaderData"
          :fields="profileHeaderFields"
          data-testid="student-detail-profile-header"
        />

        <AdvisingSessions v-if="hasAiAdvisingChatbotEnabled" :user-okta-id="oktaId"/>

        <SkeletonLoader
          v-if="!dataLoaded && !experiences?.length && !orientations?.length"
          height="h-64"
          class="mb-6"
          data-testid="student-detail-experiences-orientations-loader"
        />
        <ProgramSessionCollapsiblePanel
          v-for="experience in experiences"
          v-else
          :key="experience.programId"
          :todos-section-enabled="todosSectionEnabled"
          :can-view-submissions="canViewSubmissions"
          :student-okta-id="oktaId"
          :application-id="experience.applicationId"
          :todos="todosSubmissions.filter((todo) => todo.application_id === experience.applicationId)"
          :todos-loading="todosSubmissionsLoading"
          :program-session="experience"
          :orientations="orientations.filter((orientation) => orientation.orderId === experience.orderId)"
          :data-loaded="dataLoaded"
        />
        <SkeletonLoader
          v-if="!Object.values(activityData).some((value) => value)"
          height="h-28"
          data-testid="student-detail-recent-activity-loader"
        />
        <div
          v-else
          class="bg-white shadow-md rounded-lg p-4 mb-6"
          data-testid="student-detail-recent-activity-container"
        >
          <RecentActivity :activity-data="activityData" data-testid="student-detail-recent-activity-section" />
        </div>
      </div>

      <div v-if="!studentData" class="flex flex-col gap-4 p-4 md:w-1/4" data-testid="student-detail-sidebar-loader">
        <SkeletonLoader height="h-20" />
        <SkeletonLoader height="h-full" />
      </div>

      <StudentDetailsSidebar v-if="studentData" data-testid="student-detail-sidebar">
        <template #actions>
          <ActionButton
            v-if="hasApiEmployeeStudentViewPermission"
            button-text="STUDENT VIEW"
            aria-label="Impersonate Student"
            :default-class="'bg-teal-700 text-white p-4 w-full font-semibold'"
            :loading-class="'bg-teal-700 text-white p-4 w-full font-semibold opacity-50 pointer-events-none'"
            :on-click-action="impersonateStudent"
            :loading-icon-enabled="true"
            data-test-id="impersonate-student-button"
          />
        </template>
        <DetailsSection
          v-if="filteredSidebarData && Object.keys(filteredSidebarData).length"
          title="Details"
          :data="filteredSidebarData"
          data-testid="student-detail-details-section"
        />
      </StudentDetailsSidebar>
    </div>

    <ErrorMessage
      v-else
      :message="error?.message"
      :raw-error="error?.details"
      data-testid="student-detail-error-message"
    />
  </div>
</template>
